import React, { MouseEventHandler, useCallback, useMemo } from 'react'
import { SubmitHandler, Controller } from 'react-hook-form'
import { ControlledTextArea } from 'transfix-form/components'
import ControlledAutocompleteFacility from 'transfix-form/components/ControlledAutocompleteFacility'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { FormIf } from 'transfix-form/components/FormIf'
import { Button } from 'transfix-ui/components/Button'
import { Grid } from 'transfix-ui/core'
import { IAwardedLaneFragment } from 'apps/marketplace/pages/awarded-lanes/graphql/AwardedLaneFragment'
import { ButtonGroup } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import { findEquipment } from 'constants/equipment'
import { useUpdateAwardedLaneMutation } from '../../graphql/UpdateAwardedLane'
import { getAwardedLaneLoadingMethod } from '../helpers/getAwardedLaneLoadingMethod'
import { getErrorMessage } from './helpers/getErrorMessage'
import { parseFormInput } from './helpers/parseFormInput'
import { IUseLaneDetailsFormProps, useLaneDetailsForm } from './hooks/useLaneDetailsForm'
import { TLaneDetailsSchema, WeightType } from './schema/LaneDetailsSchema'

export interface ILaneDetailsFormProps {
  awardedLane: IAwardedLaneFragment
  defaultValues: IUseLaneDetailsFormProps['defaultValues']
  onCancelClick: MouseEventHandler<HTMLButtonElement>
  /**
   * @example (data) => {}
   */
  onSubmitSuccess: () => void
  /**
   * @example (errors) => {}
   */
  onSubmitError?: (errors: string[]) => void
}

export const LaneDetailsForm = ({
  awardedLane,
  defaultValues = {},
  onCancelClick,
  onSubmitSuccess,
  onSubmitError = () => {},
}: ILaneDetailsFormProps) => {
  const {
    equipment,
    isDeliveryDropTrailer,
    isPickupDropTrailer,
    shipperApplication: { id: shipperApplicationId },
  } = awardedLane
  const { name: equipmentName } = findEquipment(equipment?.parent?.id)
  const [updateAwardedLane] = useUpdateAwardedLaneMutation()

  const { control, errors, handleSubmit } = useLaneDetailsForm({
    defaultValues,
  })

  const onSubmit: SubmitHandler<TLaneDetailsSchema> = useCallback(
    async formInput => {
      try {
        const { data } = await updateAwardedLane({
          variables: {
            input: parseFormInput(awardedLane, formInput),
          },
        })

        const { updateShipperRate: result } = data ?? {}
        const { errors } = result ?? {}

        if (errors && errors.length > 0) {
          const messages = errors.map(({ message }) => message)
          onSubmitError(messages)
        } else {
          onSubmitSuccess()
        }
      } catch (err) {
        onSubmitError(['An error occurred while updating the Awarded Lane.'])
      }
    },
    [awardedLane, onSubmitError, onSubmitSuccess, updateAwardedLane]
  )

  const loadingMethod = useMemo(
    () => getAwardedLaneLoadingMethod(isPickupDropTrailer, isDeliveryDropTrailer),
    [isPickupDropTrailer, isDeliveryDropTrailer]
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-test='LaneDetailsForm'>
      <Grid container spacing={2}>
        <Grid container item spacing={4}>
          <Grid item>
            <strong>Equipment Type</strong>
          </Grid>

          <Grid item>{equipmentName ?? DISPLAY.empty}</Grid>
        </Grid>

        <Grid container item spacing={4}>
          <Grid item>
            <strong>Loading Method</strong>
          </Grid>

          <Grid item>{loadingMethod}</Grid>
        </Grid>

        <Grid item xs={12}>
          <ControlledTextField
            name='commodity'
            control={control}
            TextInputProps={{
              label: 'Commodity',
              placeholder: 'Dry Goods',
              fullWidth: true,
              validationMessage: getErrorMessage(errors, 'commodity'),
              error: !!getErrorMessage(errors, 'commodity'),
              dataTest: 'input-commodity',
            }}
          />
        </Grid>

        <Grid container item spacing={2}>
          <FormIf control={control} name='weightType' when={WeightType.range}>
            <Grid item xs>
              <ControlledTextField
                name='minWeight'
                control={control}
                TextInputProps={{
                  label: 'Min Weight',
                  placeholder: '30,000 lbs',
                  type: 'number',
                  validationMessage: getErrorMessage(errors, 'minWeight'),
                  error: !!getErrorMessage(errors, 'minWeight'),
                  dataTest: 'input-minWeight',
                }}
              />
            </Grid>
          </FormIf>

          <Grid item xs>
            <ControlledTextField
              name='maxWeight'
              control={control}
              TextInputProps={{
                label: 'Max Weight',
                placeholder: '45,000 lbs',
                type: 'number',
                validationMessage: getErrorMessage(errors, 'maxWeight'),
                error: !!getErrorMessage(errors, 'maxWeight'),
                dataTest: 'input-maxWeight',
              }}
            />
          </Grid>

          <Grid item xs={3}>
            <Controller
              control={control}
              name='weightType'
              render={({ onChange, value }) => {
                const handleChange = (weightType: string) => {
                  onChange(weightType)
                }

                return (
                  <ButtonGroup onChange={handleChange} value={value} label='Weight Type'>
                    <Button value={WeightType.single} data-test='single-toggle'>
                      Single
                    </Button>
                    <Button value={WeightType.range} data-test='range-toggle'>
                      Range
                    </Button>
                  </ButtonGroup>
                )
              }}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <ControlledTextField
            name='offerLeadTime'
            control={control}
            TextInputProps={{
              label: 'Offer Lead Time',
              placeholder: '2 Days',
              fullWidth: true,
              type: 'number',
              validationMessage: getErrorMessage(errors, 'offerLeadTime'),
              error: !!getErrorMessage(errors, 'offerLeadTime'),
              dataTest: 'input-offerLeadTime',
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <ControlledAutocompleteFacility
            name='pickupFacility'
            control={control}
            shipperApplicationId={shipperApplicationId}
            AutocompleteFacilityProps={{
              label: 'Pickup Facility',
              fullWidth: true,
              dataTest: `input-pickupFacility`,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <ControlledAutocompleteFacility
            name='deliveryFacility'
            control={control}
            shipperApplicationId={shipperApplicationId}
            AutocompleteFacilityProps={{
              label: 'Dropoff Facility',
              fullWidth: true,
              dataTest: `input-deliveryFacility`,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <ControlledTextArea
            name='note'
            control={control}
            TextAreaProps={{
              label: 'Additional Notes',
              placeholder: 'Write something...',
              size: 'medium',
              invalid: !!getErrorMessage(errors, 'note'),
              dataTest: 'input-note',
            }}
          />
        </Grid>

        <Grid container item justifyContent='flex-end' spacing={2}>
          <Grid item>
            <Button
              color='primary'
              onClick={onCancelClick}
              variant='outlined'
              data-test='cancel-button'>
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <Button type='submit' color='primary' data-test='submit-button'>
              Save Changes
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}
